// @ts-nocheck
import {
  VictoryAccessibleGroup,
  VictoryAxis,
  VictoryChart,
  VictoryGroup,
  VictoryLabel,
  VictoryLegend,
  VictoryLine,
  VictoryScatter,
  VictoryTheme,
  VictoryTooltip
} from 'victory'

import { ToolTipLabel } from '../../components/ToolTipLabel'
import LegendButton from '../../components/LegendButton'

import { getWaterMonth, getSymbol, getMonth, colorArray } from 'utils'
import {
  CurrentStreamFlowType,
  CleanedChartDataType,
  CleanedCsvDataType
} from '../../interfaces'
import { useState, useEffect, useId } from 'react'
import DownloadCSV from '../../../shared/DownloadCSV'

// bitmask values
const YEAR_1 = 1 //00000001
const YEAR_2 = 2 //00000010
const YEAR_3 = 4 //00000100
const YEAR_4 = 8 //00001000
const YEAR_5 = 16 //00010000
const YEAR_6 = 32 //00100000
const YEAR_7 = 64 //01000000
const YEAR_8 = 128 //10000000
const YEAR = [YEAR_1, YEAR_2, YEAR_3, YEAR_4, YEAR_5, YEAR_6, YEAR_7, YEAR_8]

const CurrentStreamFlow = ({ data, width }: any) => {
  // toggle for using calendar months or streamflow months
  const [useCalendar, setUseCalendar] = useState(true)
  // labels for legend
  const [label, setLabel] = useState<Array<{ name: string; symbol?: { type: string } }>>()
  // colors for graph
  const [colors, setColors] = useState(colorArray ? colorArray : [])
  // colors for legend
  const [legendColors, setLegendColors] = useState(colorArray ? colorArray : [])

  // data for graph
  const [startData, setStartData] = useState<CleanedChartDataType>(null)
  const [filtered, setFiltered] = useState<CleanedChartDataType>(null)
  const [csvData, setCsvData] = useState<CleanedCsvDataType>(null)

  // bitmask - boolean on/ off per year
  const [mask, setMask] = useState(
    YEAR_1 | YEAR_2 | YEAR_3 | YEAR_4 | YEAR_5 | YEAR_6 | YEAR_7 | YEAR_8
  )

  // filters startData based on bitmask
  useEffect(() => {
    if (startData && mask) {
      // @ts-ignore
      setFiltered(startData.filter((value, index) => mask & (1 << index)))
    }
  }, [startData, mask, setFiltered])

  // setup data for calendar year
  useEffect(() => {
    if (
      useCalendar &&
      data &&
      data.calendarYearData &&
      data.calendarYearPreAverageData &&
      data.calendarYearPostAverageData
    ) {
      const pre = {
        Year: `1911 - 1974 avg (${data.calendarYearPreAverageData[
          data.calendarYearPreAverageData.length - 1
        ].y
          } GL)`,
        Data: data.calendarYearPreAverageData.map(({ x, y }) => ({
          x,
          y,
          symbol: getSymbol(0)
        }))
      }
      const post = {
        Year: `Post 1975 avg (${data.calendarYearPostAverageData[
          data.calendarYearPostAverageData.length - 1
        ].y
          } GL)`,
        Data: data.calendarYearPostAverageData.map(({ x, y }) => ({
          x,
          y,
          symbol: getSymbol(1)
        }))
      }

      const cleaned = data.calendarYearData.map((x, index) => {
        const symbol = getSymbol(index + 2)
        return {
          Year: `${x.Year} (${x.Data[x.Data.length - 1].y} GL)`,
          Data: x.Data.map(({ x, y }) => ({ x, y, symbol }))
        }
      })
      const newData = [pre, post, ...cleaned]
      // @ts-ignore
      setStartData(newData)
    }
  }, [data, useCalendar])

  // setup data for csv data
  useEffect(() => {
    if (data && data.calendarYearCsvData) {
      // @ts-ignore
      setCsvData(data.calendarYearCsvData)
    }
  }, [data])

  // setup data from streamflow year
  useEffect(() => {
    if (
      !useCalendar &&
      data &&
      data.waterYearData &&
      data.waterYearPreAverageData &&
      data.waterYearPostAverageData
    ) {
      const pre = {
        Year: `1911 - 1974 avg (${data.waterYearPreAverageData[data.waterYearPreAverageData.length - 1]
          .y
          } GL)`,
        Data: data.waterYearPreAverageData.map((d, i) => ({
          x: i + 1,
          y: d.y,
          symbol: getSymbol(0)
        }))
      }
      const post = {
        Year: `Post 1975 avg (${data.waterYearPostAverageData[
          data.waterYearPostAverageData.length - 1
        ].y
          } GL)`,
        Data: data.waterYearPostAverageData.map((d, i) => ({
          x: i + 1,
          y: d.y,
          symbol: getSymbol(1)
        }))
      }

      const cleaned = data.waterYearData.map((x, index) => ({
        Year: `${x.Year} (${x.Data[x.Data.length - 1].y} GL)`,
        Data: x.Data.map((d, i) => ({
          x: i + 1,
          y: d.y,
          symbol: getSymbol(index + 2)
        }))
      }))
      const newData = [pre, post, ...cleaned]
      // @ts-ignore
      setStartData(newData)
    }
  }, [data, useCalendar])

  // filters colors based on bitmask
  useEffect(() => {
    if (mask && setColors) {
      setColors(colorArray.filter((_value, index) => mask & (1 << index)))
    }
  }, [mask, setColors])

  // changes colors based on bitmask
  useEffect(() => {
    if (colorArray) {
      setLegendColors(
        colorArray.map((value, index) =>
          mask & (1 << index) ? value : '#6E6E6E'
        )
      )
    }
  }, [colorArray, mask, setLegendColors])

  // sets up data for legend labels
  useEffect(() => {
    if (startData) {
      if (startData.length > 0) {
        const labelData = startData.map((y, i) => {
          if (y && y.Data && y.Year) {
            return { name: `${y.Year}`, symbol: { type: getSymbol(i) } }
          } else {
            return { name: `- - -` }
          }
        })

        setLabel(labelData)
      }
    }
  }, [startData])

  const id = useId()

  return (
    <>
      <VictoryChart
        desc='line chart plotting current streamflow'
        theme={VictoryTheme.material}
        domainPadding={{ x: [10, 5] }}
        padding={{
          left: 90,
          right: 0,
          top: 40,
          bottom: width > 600 ? 250 : 380
        }}
        width={width}
        height={width > 600 ? 650 : 800}
        style={{
          parent: {
            width,
            height: width > 740 ? 630 : width > 490 ? 700 : 1024
          }
        }}
      >
        {/* lines from data */}
        <VictoryGroup
        groupComponent={<VictoryAccessibleGroup aria-label="plot of current streamflow" />}
        >
          {filtered?.map((d, i) => (
            <VictoryLine
              key={'line' + i}
              data={d.Data}
              labels={({ datum }) => {
                return datum.x === 11 && i < 2 ? d.Year : null
              }}
              style={{
                data: {
                  stroke: colors[i],
                  strokeWidth: () => (i === 0 ? 4 : 1),
                  strokeLinecap: 'round'
                }
              }}
            />
          ))}
          {filtered?.map((d, i) => (
            <VictoryScatter
              key={'scatter' + i}
              data={d.Data}
              size={5}
              style={{
                data: {
                  fill: colors[i]
                }
              }}
              labels={(l) => l}
              minBubbleSize={8}
              labelComponent={
                <VictoryTooltip
                  cornerRadius={4}
                  orientation="bottom"
                  pointerOrientation="bottom"
                  flyoutWidth={i < 2 ? 190 : 120}
                  flyoutHeight={60}
                  pointerWidth={150}
                  constrainToVisibleArea
                  flyoutStyle={{
                    fill: '#ffffff',
                    stroke: (d) => colors[i]
                  }}
                  labelComponent={
                    <ToolTipLabel
                      year={String(d.Year).split('(')[0]}
                      color={colors[i]}
                      unit=" GL"
                      isStreamFlowMonths={!useCalendar}
                    />
                  }
                />
              }
            />
          ))}
        </VictoryGroup>
        {/* x axis */}
        <VictoryAxis
          orientation="bottom"
          width={width}
          tickValues={[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13]}
          tickFormat={(x) => {
            if (!x || x < 0) return "";
            return useCalendar ? getMonth(Number(x)) : getWaterMonth(Number(x))
          }}
          style={{
            axis: { stroke: '#C7C7C7', strokeWidth: 1, strokeLength: 2 },
            axisLabel: {
              fontSize: width > 360 ? 16 : 14,
              fontFamily: 'Arial'
            },
            grid: { stroke: 'transparent', strokeWidth: 0, strokeLength: 0 },
            ticks: { stroke: 'transparent' }
          }}
          fixLabelOverlap
          tickLabelComponent={
            <VictoryLabel
              id={(i) => `x:${id}:${i.index}`}
              dy={width < 600 ? 0 : 10}
              dx={width > 600 ? 0 : 10}
              textAnchor={width > 600 ? 'middle' : 'start'}
              angle={width < 600 ? 60 : 0}
              style={{
                fontSize: width > 360 ? 16 : 14,
                fontFamily: 'Arial',
                fontWeight: 'bold',
                fill: '#6E6E6E'
              }}
            />
          }
        />
        {/* y axis */}
        {data && startData && (
          <VictoryAxis
            dependentAxis
            minDomain={{ y: 0 }}
            tickFormat={(x) => `${x} GL`}
            style={{
              axis: { stroke: 'transparent' },
              axisLabel: {
                fontSize: width > 360 ? 16 : 14,
                fontFamily: 'Arial'
              },
              grid: { stroke: '#C7C7C7', strokeWidth: 1 },
              ticks: { stroke: 'transparent' },
              tickLabels: {
                fontSize: width > 360 ? 16 : 14,
                fill: '#6E6E6E',
                fontFamily: 'Arial'
              }
            }}
            tickLabelComponent={
              <VictoryLabel
                id={(i) => `y:${id}:${i.index}`}
                style={{
                  fontSize: width > 360 ? 16 : 14,
                  fill: '#6E6E6E',
                  fontFamily: 'Arial',
                  fontWeight: 'bold'
                }}
              />
            }
          />
        )}
        {label && (
          <VictoryLegend
            x={
              width > 740
                ? width / 2 - 345
                : width > 490
                  ? width / 2 - 245
                  : width / 2 - 120
            }
            y={width > 600 ? 470 : 500}
            width={width}
            orientation="horizontal"
            itemsPerRow={width > 740 ? 3 : width > 490 ? 2 : 1}
            gutter={100}
            rowGutter={{ top: 20, bottom: 5 }}
            style={{ border: { stroke: 'transparent' } }}
            colorScale={legendColors}
            data={label}
            labelComponent={
              <VictoryLabel
                id={(i) => `ledg:${id}:${i.index}`}
                className="clickable"
                tabIndex={0}
                style={{
                  fontSize: width > 360 ? 16 : 14,
                  fill: '#6E6E6E',
                  fontFamily: 'Arial',
                  fontWeight: 'bold'
                }}
                backgroundStyle={{
                  fill: 'transparent',
                  stroke: (d) => {
                    if (label) {
                      const names = label.map((x) => x.name)
                      const index = names.indexOf(d?.datum?.name)
                      //console.log(names, index)
                      return legendColors[index] === '#6E6E6E'
                        ? '#C7C7C7'
                        : '#007497'
                    }
                    return '#007497'
                  },
                  strokeWidth: '2'
                }}
                backgroundPadding={[
                  {
                    top: 10,
                    right: 20,
                    bottom: 10,
                    left: 30
                  }
                ]}
                dy={-10}
              />
            }
            events={[
              {
                target: 'labels',
                eventHandlers: {
                  onKeyUp: (e) => {
                    // @ts-ignore check even type
                    if (e.keyCode === 13) {
                      return [
                        {
                          target: 'data',
                          mutation: (props) => {
                            const fill = props.style && props.style.fill
                            if (fill === '#6E6E6E') {
                              setMask(YEAR[props.index] | mask)
                            } else {
                              setMask(mask & ~YEAR[props.index])
                            }
                          }
                        }
                      ]
                    }
                  },
                  onClick: () => {
                    return [
                      {
                        target: 'data',
                        mutation: (props) => {
                          const fill = props.style && props.style.fill
                          if (fill === '#6E6E6E') {
                            setMask(YEAR[props.index] | mask)
                          } else {
                            setMask(mask & ~YEAR[props.index])
                          }
                        }
                      }
                    ]
                  }
                }
              }
            ]}
          />
        )}
      </VictoryChart>
      <hr />
      <div className="flex flex-wrap w-full items-center justify-center my-8 relative">
        <LegendButton
          text="Calendar year"
          checked={useCalendar}
          handleChange={() => {
            setUseCalendar(true)
          }}
        />
        <LegendButton
          text="Water year (May-Apr)"
          checked={!useCalendar}
          handleChange={() => {
            setUseCalendar(false)
          }}
        />
      </div>
      {csvData && (
        <div className="w-full flex justify-center sm:justify-end mt-8 mb-16">
          <DownloadCSV
            jsonData={csvData}
            filename="streamflow.csv"
            linkname="Download current streamflow data CSV"
          />
        </div>
      )}
    </>
  )
}

export default CurrentStreamFlow
